<script setup lang="ts">
import { api } from '@/api';
import {
  ISingleSystemSetupParameter,
  QaEquipmentRtSystemDto,
  QaRtSystemTemplateContainerDto,
  TaskItemQaEquipmentSetupDto,
  TaskTypeDescription,
} from '@/api/generator';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { edit } from '@/components/icons/icons.ts';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import { useToggle } from '@vueuse/core';
import { useField, useFieldArray, useSetFieldValue } from 'vee-validate';
import { computed, onMounted, ref, toRef } from 'vue';
import { toCreateTaskItemQaEuipmentSetupDto } from '../mapping';
import FormAddButton from './FormAddButton.vue';
import QaSectionGroupParameters from './QaSectionGroupParameters.vue';
import QaSectionSystemSelection from './QaSectionSystemSelection.vue';
import { loadQaRtSystemTemplateContainers } from './QaSectionUtil';
import QaSetupParameterAddDialog from './QaSetupParameterAddDialog.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';

const props = defineProps<{
  name: string; // taskItemAreas[{areaIndex}].qaEquipmentSetups[{groupIndex}]
  taskType: TaskTypeDescription;
  alreadyUsedIDs?: string[];
  alreadyUsedGroupNames?: string[];
}>();

const alreadyUsedIDsOutsideGroup = computed(() => {
  const usedIdsInGroup = displayQaEquipmentRtSystemDtos.value.map((dto) => dto.id);
  return props.alreadyUsedIDs?.filter((id) => !usedIdsInGroup.includes(id));
});

const alreadyUsedGroupNamesOutsideGroup = computed(() => {
  return props.alreadyUsedGroupNames?.filter((gn) => taskItemQaEquipmentSetupDto.value.name !== gn);
});

// vee validate
const { value: taskItemQaEquipmentSetupDto } = useField<TaskItemQaEquipmentSetupDto>(
  toRef(() => `${props.name}`),
);

const { replace: replaceSingleSetupParameters, fields: qaParameterValues } =
  useFieldArray<ISingleSystemSetupParameter>(toRef(() => `${props.name}.singleSetupParameters`));

const { value: supportSystemEntityIds, setValue: replaceSupportSystemIDsArray } = useField<
  string[]
>(toRef(() => `${props.name}.supportSystemEntityIds`));

const setGroupName = useSetFieldValue<string>(toRef(() => `${props.name}.name`));

const emits = defineEmits<{
  removeItem: [];
}>();

const handleRemove = () => {
  emits('removeItem');
};

const mode = useFormMode();

const qaRtSystemTemplateContainerDtos = ref<QaRtSystemTemplateContainerDto[] | undefined | null>(
  undefined,
);

const getSupportSystemForId = (id: string): QaEquipmentRtSystemDto | undefined => {
  return qaRtSystemTemplateContainerDtos.value
    ?.flatMap((item) => item.entries || [])
    .map((innerItem) => innerItem.system)
    .find((system) => system?.id === id);
};

const displayQaEquipmentRtSystemDtos = computed<QaEquipmentRtSystemDto[]>(() => {
  const items = supportSystemEntityIds.value?.map((id) => getSupportSystemForId(id)) ?? [];
  return items.filter((item): item is QaEquipmentRtSystemDto => item !== undefined);
});

// modal states
const editModalOpen = ref(false);
const [isQaParameterDialogOpen, toggleQaParameterDialog] = useToggle(false);

async function handleSupportSystemSetupUpdate(supportSystemIds: string[], groupName: string) {
  editModalOpen.value = false;

  const { result } = await api.taskItemsApi.getTaskItemQaEquipmentSetupTemplate({
    getQaEquipmentSetupTemplateDto: {
      taskType: props.taskType,
      supportSystemEntityIds: supportSystemIds,
      previousQaEquipmentSetup:
        taskItemQaEquipmentSetupDto.value && taskItemQaEquipmentSetupDto.value.singleSetupParameters
          ? toCreateTaskItemQaEuipmentSetupDto(taskItemQaEquipmentSetupDto.value)
          : null,
    },
  });

  if (result?.singleSetupParameters) {
    replaceSingleSetupParameters(result.singleSetupParameters);
  }

  setGroupName(groupName);
  replaceSupportSystemIDsArray(result?.supportSystemEntityIds ?? [], true);
}

onMounted(() => {
  loadQaRtSystemTemplateContainers(
    props.taskType,
    (result) => (qaRtSystemTemplateContainerDtos.value = result),
    taskItemQaEquipmentSetupDto.value?.supportSystemEntityIds || [],
  );
});

const checkIfParameterMightGoMissingAlertShouldBeDisplayedInQaSectionModal = () => {
  const enabledParameters = qaParameterValues.value
    .filter((item) => item.value.isEnabled)
    .map((item) => item.value);

  return enabledParameters?.length;
};

// Computed property for all parameters (for the select button)
const showSelectSetupParameterButton = computed(
  () => qaParameterValues.value.map((item) => item.value).length,
);
</script>

<template>
  <div>
    <InlineValidationError class="inline-error dense mt-3 mb-3" ignoreTouched :name="name" />

    <InlineValidationError
      class="inline-error dense mt-3 mb-3"
      ignoreTouched
      validateOnMount
      :name="`${name}.supportSystemEntityIds`"
    />

    <v-card
      class="container-level-2"
      :class="{
        'overflow-visible': taskType === 'BeamConstancyCheck',
        'task-type-manual': taskType === 'ManualTask',
        'task-type-bcc': taskType === 'BeamConstancyCheck',
      }"
    >
      <v-card-item>
        <v-card-title>
          <h5>
            {{ taskItemQaEquipmentSetupDto?.name }}
          </h5>
          <VSpacer />

          <TextVBtn
            v-if="mode !== 'details'"
            :icon="edit"
            size="small"
            @click="() => (editModalOpen = true)"
          />
          <!-- remove button -->
          <PtwDeleteButton
            v-if="mode !== 'details' && taskType === 'ManualTask'"
            @delete="handleRemove"
          />
        </v-card-title>
      </v-card-item>

      <v-card-text>
        <!-- tiles -->
        <div
          v-if="displayQaEquipmentRtSystemDtos && displayQaEquipmentRtSystemDtos.length"
          class="tile-container mb-6"
        >
          <RTSystemTile
            v-for="displayQaEquipmentRtSystemDto in displayQaEquipmentRtSystemDtos"
            :key="displayQaEquipmentRtSystemDto?.id"
            :item="displayQaEquipmentRtSystemDto"
          />
        </div>

        <!-- add parameter button -- manual task only -->
        <v-card class="container-level-3">
          <v-card-item>
            <v-card-title>
              <h6>{{ $t('SetupParameters') }}</h6>
              <VSpacer />
              <FormAddButton
                v-if="
                  mode !== 'details' && taskType === 'ManualTask' && showSelectSetupParameterButton
                "
                :label="'AddSetupParameter'"
                @click="toggleQaParameterDialog(true)"
              />
            </v-card-title>
          </v-card-item>
          <v-card-text>
            <!-- QA parameters -->
            <QaSectionGroupParameters :name="`${name}.singleSetupParameters`" :taskType="taskType"
          /></v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- add parameter dialog -->
    <QaSetupParameterAddDialog
      v-model="isQaParameterDialogOpen"
      :taskType="taskType"
      :parameters="qaParameterValues.map((item) => item.value)"
      @addParameters="
        (r) => {
          replaceSingleSetupParameters(r);
        }
      "
    />

    <!-- edit dialog  -->
    <QaSectionSystemSelection
      v-model="editModalOpen"
      :name="name"
      :taskType="taskType"
      :qaRtSystemTemplateContainerDtos="qaRtSystemTemplateContainerDtos || []"
      :groupName="taskItemQaEquipmentSetupDto?.name || ''"
      :alreadyUsedIDs="alreadyUsedIDsOutsideGroup"
      :alreadyUsedGroupNames="alreadyUsedGroupNamesOutsideGroup"
      :supportSystemEntityIds="taskItemQaEquipmentSetupDto?.supportSystemEntityIds"
      :notification="
        checkIfParameterMightGoMissingAlertShouldBeDisplayedInQaSectionModal()
          ? {
              type: 'warning',
              message: $t('SetupParametersMightGoMissing'),
            }
          : undefined
      "
      @submit="handleSupportSystemSetupUpdate"
    />
  </div>
</template>

<style lang="scss" scoped>
.v-card {
  &.content-block {
    &.task-type-bcc {
      .v-card-item {
        .v-card-title {
          background-color: transparent;
        }
      }

      .v-card-text {
        padding: 0 !important;
      }
    }
    > .v-card-item .v-card-title {
      background: rgb(var(--v-theme-primary-lighten-5)) !important;
      padding: 4px 16px;
    }
  }
}
.v-card .tile-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 16px;
}
</style>
